import { Image } from '@timelyapp/react';
import classNames from 'classnames';
import DurationFormat from 'components/DurationFormat';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import { Briefcase, Ellipsis } from 'components/Icons';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import { TIMELINE_BAR_HEIGHT_THRESHOLD } from 'constants/memory';
import MemoryDnD from 'constants/memory-dnd';
import { DEFAULT_MEMORY_COLOR } from 'constants/timeline';
import React from 'react';
import { useDrag } from 'react-dnd';
import type { toggleHourTimestampFromMemory } from 'redux/modules/hours.actions';
import type {
  deleteEntryIds,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  predictEntryIdsToProjectId,
} from 'redux/modules/timeline.actions';
import type { MappedAction } from 'types/redux';
import type { MemoryWithTimespan } from 'types/timeline';
import commonStyles from './MemoryTimeline.module.scss';

interface Props {
  hideShortDurationMemoryTitles: boolean;
  memory: MemoryWithTimespan;
  showDebugInfo: boolean;
  useProjectColorOnMemories?: boolean;
  toggleTimestampFromMemory: MappedAction<typeof toggleHourTimestampFromMemory>;
  toggleSelectEntryId: (entry_id: number) => void;
  markEntryIdsAsSpam: MappedAction<typeof markEntryIdsAsSpam>;
  markEntryIdsAsNotSpam: MappedAction<typeof markEntryIdsAsNotSpam>;
  predictEntryIdsToProjectId: MappedAction<typeof predictEntryIdsToProjectId>;
  deleteEntryIds: MappedAction<typeof deleteEntryIds>;
}

export const MemoryItemOnTimeline: React.FC<Props> = props => {
  const [_, drag] = useDrag(() => ({
    type: MemoryDnD.TYPE,
    item: {
      memoryIds: [props.memory.id],
      source: { area: MemoryDnD.AREA.MemoryInbox },
    },
    collect: monitor => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  }));

  const handleToggleSpam = (event: React.SyntheticEvent): void => {
    event.stopPropagation(); // Stop container click-to-select handler

    if (props.memory.isSpam) {
      props.markEntryIdsAsNotSpam(props.memory.id);
    } else {
      props.markEntryIdsAsSpam(props.memory.id);
    }
  };

  const handleDeleteMemory = (event: React.SyntheticEvent): void => {
    event.stopPropagation(); // Stop container click-to-select handler
    props.deleteEntryIds(props.memory.id);
  };

  const handleClickMemory = () => {
    props.toggleSelectEntryId(props.memory.id);
  };

  const handleOptionClick = (
    option: { readonly action: 'delete' | 'ignore' | 'restore' },
    event: React.SyntheticEvent
  ) => {
    if (option.action === 'ignore' || option.action === 'restore') {
      handleToggleSpam(event);
    }
    if (option.action === 'delete') {
      handleDeleteMemory(event);
    }
  };

  const renderOptionMenu = () => {
    const { memory } = props;
    const options = [
      {
        action: memory.isSpam ? 'restore' : 'ignore',
        name: memory.isSpam ? 'Restore' : 'Ignore',
      },
      {
        action: 'delete',
        name: 'Delete',
        confirm: true,
        confirmMessage: 'Delete memory? This cannot be undone',
      },
    ] as const;
    return (
      <PopoverDropdownList
        stopPropagation
        fixed
        onClick={handleOptionClick}
        targetCaret={false}
        targetWrapperElement="span"
        options={options}
        target={
          <button
            type="button"
            className={commonStyles.memoryOptionsButton}
            data-testid="timeline_memory_edit"
          >
            <Ellipsis />
          </button>
        }
      />
    );
  };

  const { memory, showDebugInfo, useProjectColorOnMemories, hideShortDurationMemoryTitles } = props;
  const { timespan } = memory;

  // We might want to move this up to factory/selector?
  const isAssignedToProject = (memory.isLogged || memory.isPredicted) && !!memory.project.name;
  const showAsLogged = isAssignedToProject;
  const hasSubMemories = memory.subEntries.length > 0;
  const includeRevealableDetails = hasSubMemories || showDebugInfo; // && showAsLogged;
  // The AI signal UI leaves a lot to be desired, especially with the crazy
  const showAISignal = false; // memory.isPredictedToCurrentProject; // && !showAsLogged;

  const showDurationBar = timespan.vertical.heightPx > TIMELINE_BAR_HEIGHT_THRESHOLD;
  const memoryClass = classNames(commonStyles.memory, {
    [commonStyles.isSpam]: memory.isSpam,
    [commonStyles.isShort]: !showDurationBar,
    [commonStyles.isMuted]: hideShortDurationMemoryTitles && !showDurationBar,
    [commonStyles.isChecked]: memory.isChecked,
    [commonStyles.isLogged]: showAsLogged,
    [commonStyles.isPredicted]: memory.isPredicted,
    [commonStyles.noCardColor]: !memory.cardBgColor,
    [commonStyles.truncated]: timespan.vertical.truncated,
  });

  const cardStyle = {
    backgroundColor: memory.cardBgColor ?? undefined,
  };

  const cardClass = classNames(
    commonStyles.card,
    commonStyles[`variant-${memory.cardTextVariant}`],
    {
      [commonStyles.isPredicted]: showAISignal,
    }
  );

  // If memory is truncated, move the card into view:
  const mainStyle: React.CSSProperties | undefined =
    timespan.vertical.cardOffsetTopPx > 0
      ? {
          top: timespan.vertical.cardOffsetTopPx,
          position: 'absolute',
          left: 15,
        }
      : undefined;

  let barColor = memory.barColor;
  if (useProjectColorOnMemories) {
    barColor = memory.project?.color || DEFAULT_MEMORY_COLOR;
  }

  const barStyle = showDurationBar
    ? { height: timespan.vertical.heightPx, background: barColor }
    : undefined;

  const shouldShowTimestamp = !memory.isLogged || (memory.isLogged && memory.isChecked);

  return (
    <div
      key={memory.id}
      ref={drag}
      className={memoryClass}
      style={{ top: timespan.vertical.topPx }}
      data-testid="timeline_memory"
    >
      <div className={commonStyles.bar} style={barStyle}>
        {(memory.icon || memory.fallbackIcon) && (
          <Image
            src={memory.icon}
            fallback={memory.fallbackIcon}
            className={classNames(commonStyles.icon, 'fs-exclude')}
          />
        )}
      </div>
      <div className={commonStyles.main} style={mainStyle}>
        <div
          className={cardClass}
          style={cardStyle}
          onClick={handleClickMemory}
          data-testid="timeline_memory_card"
        >
          <div>
            <div className={classNames(commonStyles.revealOnHover, commonStyles.headerAction)}>
              {renderOptionMenu()}
            </div>
            <span
              className={classNames(commonStyles.header, 'fs-mask')}
              data-testid="timeline_memory_title"
            >
              <DiagnosticResourceId resource="memory" id={memory.id} />
              {memory.title}
            </span>
          </div>

          {/* {showAISignal && (
                <Label className={commonStyles.predictionIndicator} size="small">
                  <FontAwesome name="exclamation-circle" /> AI
                </Label>
              )} */}

          <div className={commonStyles.timestamp}>
            {shouldShowTimestamp && (
              <div className={commonStyles.hideOnHover} data-testid="timeline_memory_duration">
                {memory.duration.total_seconds === 0 ? (
                  memory.fromTime
                ) : (
                  <DurationFormat
                    format={memory.duration.total_minutes < 60 ? 'mm[min]' : 'h[h] mm[min]'}
                    value={memory.duration.total_minutes}
                  />
                )}
              </div>
            )}
            <div className={commonStyles.revealOnHover}>
              {memory.fromTime} ⇾ {memory.toTime}
            </div>
          </div>

          {!!memory.description && (
            <div
              className={classNames(
                commonStyles.description,
                commonStyles.revealOnHover,
                'fs-mask'
              )}
              data-testid="timeline_memory_description"
            >
              {memory.description}
            </div>
          )}
          {showAsLogged && (
            <div
              className={classNames(
                commonStyles.projectName,
                commonStyles.revealOnHover,
                'fs-mask'
              )}
            >
              <Briefcase />
              {memory.project.name}
            </div>
          )}
          {includeRevealableDetails && (
            <div className={commonStyles.revealOnHover}>
              {hasSubMemories && (
                <ul className={commonStyles.subMemoryList}>
                  {memory.subEntries.map(sub => {
                    return (
                      <li key={sub.id} className="fs-mask">
                        <DiagnosticResourceId resource="sub memory" id={sub.id} />

                        {sub.note}

                        <div className={commonStyles.subMemoryTimestamp}>
                          {sub.fromTime} ⇾ {sub.toTime}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {showDebugInfo && (
                <div className={commonStyles.debugInfo}>
                  <h3>Debug (Timely employees only)</h3>
                  <ul className={commonStyles.subMemoryList}>
                    {memory.id < 0 && (
                      <li>
                        <span>Synthetic</span>
                      </li>
                    )}
                    <li>
                      <span>Importance: </span>
                      <code>{memory.importance}</code>
                    </li>
                    <li>
                      <span>Group Duration: </span>
                      <code>{memory.groupDuration.formatted}</code>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryItemOnTimeline;
